import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import {
  DEFAULT_CURRENCY_CODE,
  LOCALE_ID,
  makeEnvironmentProviders,
} from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
} from '@angular/material/core';
import { DateFnsConfigurationService } from 'ngx-date-fns';

import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { getLocaleConfig, getPreferredLocaleId } from './locale-data';
import {
  LOCALE_CONFIG,
  LOCALIZATION_CONFIG,
  LocalizationConfig,
} from './localization.model';
import { LocalizationService } from './localization.service';

const dateFnsMatDateFormats: MatDateFormats = {
  parse: {
    dateInput: 'P',
  },
  display: {
    dateInput: 'P',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'PP',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

function _provideLocalization(config: LocalizationConfig) {
  return [
    LocalizationService,
    { provide: LOCALIZATION_CONFIG, useValue: config },
    {
      provide: LOCALE_ID,
      useValue: config.defaultLocale || getPreferredLocaleId(),
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: config.defaultCurrencyCode || 'GBP',
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: config.datePipeConfig || {
        dateFormat: 'shortDate',
      },
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: dateFnsLocaleFactory,
      deps: [LOCALE_ID],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: dateFnsMatDateFormats,
    },
    {
      provide: DateFnsConfigurationService,
      useFactory: dateFnsConfigFactory,
      deps: [LOCALE_ID],
    },
    {
      provide: LOCALE_CONFIG,
      useFactory: localeConfigFactory,
      deps: [LOCALE_ID],
    },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
    },
  ];
}

export function provideLocalization(config: LocalizationConfig) {
  return makeEnvironmentProviders(_provideLocalization(config));
}

function localeConfigFactory(locale: string) {
  return getLocaleConfig(locale);
}

function dateFnsLocaleFactory(locale: string) {
  const localeId = locale || getPreferredLocaleId();

  return getLocaleConfig(localeId).dateFnsLocale;
}

function dateFnsConfigFactory(locale: string) {
  const localeId = locale || getPreferredLocaleId();

  const dateFnsLocale = getLocaleConfig(localeId).dateFnsLocale;

  const dateFnsConfig = new DateFnsConfigurationService();
  dateFnsConfig.setLocale(dateFnsLocale);
  return dateFnsConfig;
}
